import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../context/NotificationContext';
import { getApiUrl } from '../config/api';

export const useScriptToPromptNavigation = (projectId?: string) => {
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const handleProceedWithScript = async (
    scriptId: string, 
    promptTypes: string[], 
    useConsistentCharacters: boolean = false,
    scripts: any[] = []
  ) => {
    try {
      console.log('🚀 Creating draft prompts with:', { scriptId, promptTypes, useConsistentCharacters, projectId });
      
      // Get the full script data
      const selectedScript = scripts.find(s => s.id === scriptId);
      if (!selectedScript) {
        console.error('❌ Script not found for ID:', scriptId);
        return;
      }

      console.log('✅ Found script:', selectedScript.title);

      // Import the ScriptAnalyzer to get actual prompt estimates and character data
      const { ScriptAnalyzer } = await import('../components/ScriptGenerator/utils/script-analyzer.util');
      const estimates = ScriptAnalyzer.getPromptEstimates(selectedScript.generatedScript);
      const breakdown = ScriptAnalyzer.parseSceneBreakdown(selectedScript.generatedScript);
      
      console.log('📊 Using actual script estimates:', estimates);
      console.log('🎭 Character breakdown:', breakdown?.characters);

      // Map prompt types to estimates
      const promptEstimates = {
        'image': estimates.image,
        'music': estimates.music,
        'sfx': estimates.sfx,
        'narration': estimates.narration,
        'characters': estimates.characters
      };

      console.log('🎯 Final prompt estimates:', promptEstimates);

      // Extract scene breakdown data
      const sceneBreakdown = ScriptAnalyzer.parseSceneBreakdown(selectedScript.generatedScript);
      console.log('🎬 Scene breakdown:', sceneBreakdown);

      // Create draft prompts for each selected type with multiple variations
      const authToken = localStorage.getItem('authToken');
      const allPromptCreationPromises = [];

      console.log('🔍 Selected prompt types:', promptTypes);
      console.log('📊 Character estimate specifically:', promptEstimates.characters);
      
      for (const promptType of promptTypes) {
        const estimateCount = promptEstimates[promptType] || 1;
        
        console.log(`📝 Creating ${estimateCount} prompts for type: ${promptType}`);
        
        // Get the specific content for this prompt type
        let specificContent = [];
        console.log("promptType=================================== ", promptType);
        switch (promptType) {
          case 'image':
            // Handle SceneData objects or fallback to strings
            if (sceneBreakdown?.imageScenes) {
              specificContent = sceneBreakdown.imageScenes.map(scene => 
                typeof scene === 'object' ? scene.description : scene
              );
            } else {
              specificContent = [`Visual content from: ${selectedScript.title}`];
            }
            break;
          case 'music':
            specificContent = sceneBreakdown?.musicCues || [`Music for: ${selectedScript.title}`];
            break;
          case 'sfx':
            specificContent = sceneBreakdown?.soundEffects || [`Sound effects for: ${selectedScript.title}`];
            break;
          case 'characters':
            // Use character data from scene breakdown
            if (sceneBreakdown?.characters) {
              specificContent = sceneBreakdown.characters.map(character => 
                typeof character === 'object' ? character.description : character
              );
            } else {
              specificContent = [`Character designs for: ${selectedScript.title}`];
            }
            break;
          case 'narration':
            // For narration, use the cleaned script content directly
            const cleanedScript = selectedScript.generatedScript
              .replace(/\[SCENE:[^\]]*\]/gi, '') // Remove scene markers
              .replace(/\[ACTION:[^\]]*\]/gi, '') // Remove action markers
              .replace(/\[SFX:[^\]]*\]/gi, '') // Remove SFX markers
              .replace(/\[MUSIC:[^\]]*\]/gi, '') // Remove music markers
              .replace(/\s+/g, ' ') // Clean up whitespace
              .trim();
            specificContent = [`Full story narration: ${cleanedScript}`];
            break;
          default:
            specificContent = [`Content for ${promptType}: ${selectedScript.title}`];
        }

        // Create prompts based on estimate count
        for (let i = 1; i <= estimateCount; i++) {
          let characterReferences = [];
          
          // For image prompts with character consistency
          if (promptType === 'image' && useConsistentCharacters && sceneBreakdown?.imageScenes && breakdown?.characters) {
            const currentIndex = i - 1;
            const currentScene = sceneBreakdown.imageScenes[currentIndex];
            
            if (currentScene && typeof currentScene === 'object' && currentScene.characterTags) {
              const sceneCharacterTags = currentScene.characterTags;
              
              characterReferences = sceneCharacterTags.map(charTag => {
                const character = breakdown.characters.find(c => c.characterId === charTag);
                return character ? {
                  characterId: character.characterId,
                  description: character.description
                } : null;
              }).filter(Boolean);
            }
          }

          const promptContent = Array.isArray(specificContent) && specificContent[i - 1] 
            ? specificContent[i - 1] 
            : specificContent[0] || `${promptType} content from ${selectedScript.title}`;

          // Format title with proper type names and brackets
          const typeMap = {
            'image': 'Image',
            'music': 'Music',
            'sfx': 'Sound Effect',
            'narration': 'Narration',
            'characters': 'Character'
          };

          // Get the proper type name from the map
          const typeName = typeMap[promptType] || 'Content';

          // Create descriptive title with brackets and type prefix
          const isMultiple = estimateCount > 1;
          const createDescriptiveTitle = (scene: string, type: string, index: number) => {
            // Take first 50 characters of scene description for title
            const shortDescription = scene.length > 50 ? scene.substring(0, 50) + '...' : scene;
            return `${type} ${index}: ${shortDescription}`;
          };

          // For character prompts, always add numbers (even for single character)
          const shouldNumberPrompt = isMultiple || promptType === 'characters';
          
          const title = shouldNumberPrompt 
            ? createDescriptiveTitle(promptContent, typeName, i)
            : `${typeName}: ${promptContent.length > 50 ? promptContent.substring(0, 50) + '...' : promptContent}`;

          // Enhanced attributes formatting
          const attributes = isMultiple
            ? `${promptType}-script-generated-variation-${i}`
            : `${promptType}-script-generated`;

          // Add script context for better LLM understanding
          const scriptContext = `Story context: ${selectedScript.userSelections?.userIdea || 'A creative story'}`;
          
          // Enhanced context for image prompts with character handling
          let contextualContent = `Specific ${typeName.toLowerCase()} requirement: ${promptContent}`;
          
          if (promptType === 'image') {
            // Add previous scenes context for visual consistency
            if (sceneBreakdown?.imageScenes) {
              const currentIndex = i - 1;
              const previousScenes = sceneBreakdown.imageScenes.slice(Math.max(0, currentIndex - 2), currentIndex);
              
              if (previousScenes.length > 0) {
                const previousContext = previousScenes.map((scene, index) => {
                  const sceneNumber = Math.max(0, currentIndex - 2) + index + 1;
                  const sceneText = typeof scene === 'object' ? scene.description : scene;
                  return `Scene ${sceneNumber}: ${sceneText}`;
                }).join('\n');
                
                const currentSceneText = typeof promptContent === 'string' ? promptContent : promptContent;
                contextualContent = `Previous scenes context:\n${previousContext}\n\nCurrent scene: ${currentSceneText}`;
              }
            }
          }
          
          const fullPromptContent = `${scriptContext}\n\n${contextualContent}`;

          const promptPayload = {
            projectId: projectId,
            title: title,
            userIdea: fullPromptContent,
            attributes: attributes,
            scriptData: {
              scriptId: selectedScript.id,
              title: selectedScript.title,
              content: selectedScript.generatedScript
            },
            ...(characterReferences.length > 0 && {
              characterMetadata: {
                characterReferences: characterReferences,
                hasCharacters: true,
                useConsistentCharacters: useConsistentCharacters
              }
            }),
            // Add characterId to promptMetadata for character reference prompts
            ...(promptType === 'characters' && {
              promptMetadata: {
                characterId: `CHAR ${i}`
              }
            })
          };

          // Handle sound effects differently - create draft then immediately update with short text
          if (promptType === 'sfx') {
            console.log("------------------------> sfx", );
            const createPromise = fetch(getApiUrl('/prompt-generator/create-draft'), {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(promptPayload)
            }).then(async (response) => {
              console.log("------------------------> response", response);
              const result = await response.json();
              console.log("------------------------> result", result);

              
              if (result.success) {
                // Immediately update with short sound effect text
                console.log(`🎵 Updating sound effect prompt ${result.id} with short text: "${promptContent}"`);
                
                const updateResponse = await fetch(getApiUrl('/prompt-generator/update'), {
                  method: 'PUT',
                  headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                    promptId: result.data.promptId,
                    generatedPrompt: promptContent // Keep the short text like "Ocean waves (varying intensities)"
                  })
                });
                
                const updateResult = await updateResponse.json();
                console.log(`✅ Sound effect prompt updated:`, updateResult);
                
                return result; // Return the original creation result
              }
              
              return result;
            });
            
            allPromptCreationPromises.push(createPromise);
          } else {
            // Regular processing for other prompt types
            const createPromise = fetch(getApiUrl('/prompt-generator/create-draft'), {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(promptPayload)
            });

            allPromptCreationPromises.push(createPromise);
          }
        }
      }

      // Wait for all prompts to be created
      const responses = await Promise.all(allPromptCreationPromises);
      
      // Handle different response formats (sound effects return promise results, others return fetch responses)
      const results = await Promise.all(responses.map(async (response) => {
        // If it's already a result object (from sound effects), return as is
        if (response && typeof response === 'object' && ('success' in response)) {
          return response;
        }
        // Otherwise, it's a fetch response that needs to be parsed
        return await response.json();
      }));

      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);

      if (successful.length > 0) {
        const totalCreated = successful.length;
        const promptSummary = promptTypes.join(', ');

        showNotification({
          title: 'Script Loaded Successfully!',
          message: `Successfully loaded ${totalCreated} draft prompts (${promptSummary}) from your script into the Prompt Generator.`,
          type: 'success'
        });

        // Navigate to prompt generator to show the created drafts
        if (projectId) {
          navigate(`/prompt-generator/${projectId}`);
        }
      }

      if (failed.length > 0) {
        console.error('Some prompts failed to create:', failed);
      }
      
    } catch (error) {
      console.error('❌ Error creating draft prompts:', error);
      showNotification({
        title: 'Loading Failed',
        message: 'Failed to load script into Prompt Generator. Please try again.',
        type: 'error'
      });
    }
    
    setShowModal(false);
  };

  const handleProceedWithoutScript = () => {
    // Navigate to prompt generator without script data
    if (projectId) {
      navigate(`/prompt-generator/${projectId}/prompt-generator`);
    } else {
      navigate('/prompt-generator');
    }
    setShowModal(false);
  };

  return {
    showModal,
    setShowModal,
    handleProceedWithScript,
    handleProceedWithoutScript
  };
};
